import { useMutation } from '@tanstack/react-query';
import { UpdateOrganizationPayForOthersEnabledDto } from 'models/organization/dto/organization.dto';
import { useApiClient } from '../../../apiClient';
import { useSelectedOrganization } from './useSelectedOrganization';

// Per-tenant gate for the "Pay for others" flow. Hits
// POST :id/pay-for-others-enabled on the API. The endpoint returns only the
// flipped flag ({ payForOthersEnabled }) — not the full aggregate — so we patch
// it into the cached selectedOrganization locally rather than blowing the
// whole org back into the Zustand store. Mirrors useUpdateBookingPaymentDeadline
// in shape and intent (single-field POST, optimistic local cache update).
export const useUpdateOrganizationPayForOthersEnabled = (
    organizationId: string,
    onSuccess: () => void,
) => {
    const apiClient = useApiClient();
    const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationKey: ['updateOrganizationPayForOthersEnabled', organizationId],
        mutationFn: (body: UpdateOrganizationPayForOthersEnabledDto) =>
            apiClient.fetchApi<
                {
                    payForOthersEnabled?: boolean;
                    payForOthersDailyCap?: number;
                    guestPlayersEnabled?: boolean;
                    guestsPerGameMax?: number;
                    payForOthersConsentRequired?: boolean;
                },
                UpdateOrganizationPayForOthersEnabledDto
            >({
                method: 'POST',
                endpoint: `organizations/${organizationId}/pay-for-others-enabled`,
                body,
            }),
        onSuccess: (data) => {
            onSuccess();
            // Only update the selected-org cache when the response is for the
            // org currently in view — defensive against the rare case where
            // the operator switches orgs while the mutation is in flight.
            if (selectedOrganization && organizationId === selectedOrganization.id) {
                setSelectedOrganization({
                    ...selectedOrganization,
                    // The API echoes each field only when the call changed it.
                    ...(data.payForOthersEnabled !== undefined
                        ? { payForOthersEnabled: data.payForOthersEnabled }
                        : {}),
                    // The API echoes the cap only when the call changed it.
                    ...(data.payForOthersDailyCap !== undefined
                        ? { payForOthersDailyCap: data.payForOthersDailyCap }
                        : {}),
                    ...(data.guestPlayersEnabled !== undefined
                        ? { guestPlayersEnabled: data.guestPlayersEnabled }
                        : {}),
                    ...(data.guestsPerGameMax !== undefined
                        ? { guestsPerGameMax: data.guestsPerGameMax }
                        : {}),
                    ...(data.payForOthersConsentRequired !== undefined
                        ? { payForOthersConsentRequired: data.payForOthersConsentRequired }
                        : {}),
                });
            }
        },
    });
};
